import { Button } from '@zoopla-eng/zds-components/button';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import {
    SearchContext,
    SearchPageType,
    SearchSource
} from '@zoopla/listing-search-context';
import {
    FormValues,
    getDuration,
    getFormValuesFromQuery,
    getQ,
    getSearchPath,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import {
    BedsMax,
    BedsMin,
    PriceMax,
    PriceMin,
    SelectBeds,
    SelectPriceForSale,
    SelectPriceToRent
} from '@zoopla/listing-search/filters/options';
import { LocationSearchInput } from '@zoopla/listing-search/input';
import {
    PropertySubType,
    PropertyType,
    PropertyTypeCheckboxes
} from '@zoopla/listing-search/property-type';
import { triggerTracking } from '@zoopla/tracking';
import { NextRouter, useRouter } from 'next/router';
import { FC, MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import { formatIdentifier } from '@zoopla/listing-search-utils';
import { getQueryParams } from '../utils/getQueryParams';
import {
    filterDividerStyle,
    filtersLabelStyle,
    filterWrapperStyle,
    locationFilterWrapperStyle,
    modalContentWrapperStyle,
    stickyWrapperUpdateButtonStyle,
    travelWrapperStyle
} from './SearchInitiationMobileModal.styles.css';
import { Duration, SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectPropertyType } from './SelectPropertyType';
import { Radius, SelectRadius } from './SelectRadius';
import { SelectTransportMode, TransportType } from './SelectTransportMode';
import { navigateToResultsPage } from '@zoopla/listing-search-utils';

type Tab = SearchPageType | 'house-prices';

const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

export type SearchInitiationMobileModalProps = {
    showRadius?: boolean;
    updateResultsText: string;
    headlineText?: string;
    headerTextOverride?: string;
    close: () => void;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results';
    searchSource: SearchSource;
};

const getIdentifier = (query: NextRouter['query']) =>
    formatIdentifier(getSearchPath(query));

const getLocation = (query: NextRouter['query']) => {
    const q = getQ(query);
    const coercedQ = Array.isArray(q) ? q[0] : q;
    if (coercedQ !== '') return coercedQ;
    return;
};

export const SearchInitiationMobileModal: FC<
    React.PropsWithChildren<SearchInitiationMobileModalProps>
> = ({
    showRadius = true,
    close,
    updateResultsText,
    searchType,
    eventCategory,
    searchSource
}) => {
    const { query, push, pathname, isReady } = useRouter();
    const [resolvedQuery, updateResolvedQuery] = useState(query);
    const [formValue, updateFormValue] = useState<FormValues>(
        getFormValuesFromQuery({ query, pathname })
    );
    const { state } = useContext(SearchContext);

    const originalQuery = useRef(
        getLocation(query) ?? state.locationByIdentifier
    );
    const selectedSearchArea = useRef<{
        id: string;
        text: string;
    } | null>(null);
    const identifier = useRef(getIdentifier(query));
    const [locationText, setLocationText] = useState(
        getLocation(query) ?? state.locationByIdentifier
    );
    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);

    useEffect(() => {
        if (isReady) {
            updateResolvedQuery(query);
            // router.isReady & query is async defaulting to an empty obj,
            // it is possible for the user to have already mutated formValues
            // so to prevent this we disable form input until router.isReady
            updateFormValue((o) => ({
                ...o,
                ...getFormValuesFromQuery({ query, pathname })
            }));
            identifier.current = getIdentifier(query);
            originalQuery.current =
                getLocation(query) ?? state.locationByIdentifier ?? '';
            setLocationText(
                getLocation(query) ?? state.locationByIdentifier ?? ''
            );
        }
    }, [query, isReady, pathname, state.locationByIdentifier]);

    const isTravelTimeSearch = Boolean(
        getDuration(resolvedQuery) && getTransportType(resolvedQuery)
    );
    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isNewHomesDevelopersSearch = searchSource === 'new-homes-dev';
    const viewType = pathname.includes('/map/') ? 'map' : 'list';

    const handleSearchProperties = async () => {
        if (!locationText) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        const eventLabel = locationText
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        if (isNewHomesDevelopersSearch) {
            await push({
                pathname: selectedSearchArea.current?.id
                    ? `/new-homes/developers/${selectedSearchArea.current.id}/`
                    : `/new-homes/developers/`,
                query: {
                    ...formValue,
                    ...(formValue.beds_max && { beds_max: formValue.beds_max }),
                    ...(formValue.beds_min && { beds_min: formValue.beds_min }),
                    ...(formValue.price_min && {
                        price_min: formValue.price_min
                    }),
                    ...(formValue.price_max && {
                        price_max: formValue.price_max
                    }),
                    property_type: formValue.property_type,
                    q: selectedSearchArea.current?.text ?? locationText
                }
            });

            return;
        }

        const defaultQueryParams = getQueryParams(searchType);

        const getActiveFilters = (allFilters: typeof formValue) => {
            const filters = [
                'added',
                'available_from',
                'beds_max',
                'beds_min',
                'bills_included',
                'chain_free',
                'duration',
                'feature',
                'furnished_state',
                'include_rented',
                'include_sold',
                'is_auction',
                'is_retirement_home',
                'is_shared_accommodation',
                'is_shared_ownership',
                'keywords',
                'new_homes',
                'pets_allowed',
                'polyenc',
                'price_frequency',
                'price_max',
                'price_min',
                'property_sub_type',
                'property_type',
                'radius',
                'reduced_price_only',
                'results_sort',
                'transport_type'
            ] as (keyof typeof formValue)[];

            return filters.reduce((acc, curr) => {
                if (curr === 'property_type' && allFilters[curr] !== '') {
                    return {
                        ...acc,
                        [curr]:
                            allFilters[curr] === 'property'
                                ? ''
                                : allFilters[curr]
                    };
                }
                const hasVal = (v: string | string[]) =>
                    Array.isArray(v) ? v.length > 0 && v[0] !== '' : v !== '';
                return {
                    ...acc,
                    ...(hasVal(allFilters[curr]) && {
                        [curr]: allFilters[curr]
                    })
                };
            }, {});
        };

        const searchParams = {
            ...defaultQueryParams,
            ...getActiveFilters(formValue),
            ...(!selectedSearchArea.current?.id
                ? {
                      q: locationText ?? '',
                      orig_q: originalQuery.current ?? '',
                      polyenc:
                          locationText === originalQuery.current
                              ? formValue.polyenc
                              : ''
                  }
                : {
                      q: selectedSearchArea.current.text,
                      orig_q: originalQuery.current ?? '',
                      geo_autocomplete_identifier:
                          selectedSearchArea.current.id,
                      polyenc:
                          selectedSearchArea.current.text ===
                          originalQuery.current
                              ? formValue.polyenc
                              : ''
                  }),
            identifier: identifier.current,
            view_type: viewType,
            // Todo: This persists the Search Service experiment flag. Remove this Search Service test flag once the migration is complete. See https://zpgltd.atlassian.net/browse/SR-1689. There are 2 others.
            ...(query['ss'] ? { ss: query['ss'] || '' } : {})
        };

        navigateToResultsPage(searchParams, push).then(() => close());
    };

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
        close();
    };

    const showError = hasSubmittedLocationSearch && !locationText;

    return (
        <div className={modalContentWrapperStyle}>
            <div className={locationFilterWrapperStyle}>
                <LocationSearchInput
                    id="filters-location-mobile"
                    onLocationInputKeyDown={handleSearchProperties}
                    onlyDisplayRemoveIconWhenActive={false}
                    value={locationText as string}
                    showError={showError}
                    onLocationSelect={({ identifier, q }) => {
                        setLocationText(q);
                        selectedSearchArea.current = {
                            id: identifier,
                            text: q
                        };
                    }}
                    onClearText={() => {
                        setLocationText('');
                        selectedSearchArea.current = null;
                    }}
                    handleInputTextChange={(value) => {
                        setLocationText(value);
                        selectedSearchArea.current = null;
                    }}
                />
            </div>

            <div className={filterDividerStyle} />

            {isTravelTimeSearch ? (
                <div className={filterWrapperStyle}>
                    <p className={`${filtersLabelStyle} ${fontGroups.ladle}`}>
                        Travel time
                    </p>
                    <div className={travelWrapperStyle}>
                        <SelectMaxTravelTime
                            value={formValue.duration as Duration}
                            onChange={(duration) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    duration: duration
                                }));
                            }}
                        />
                        <SelectTransportMode
                            value={formValue.transport_type as TransportType}
                            onChange={(transportType) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    transport_type: transportType
                                }));
                            }}
                        />
                    </div>
                </div>
            ) : showRadius ? (
                <SelectRadius
                    value={formValue.radius as Radius}
                    onChange={(radius) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            radius: radius
                        }));
                    }}
                />
            ) : null}

            <div className={filterDividerStyle} />

            {isForSaleSearch ? (
                <SelectPriceForSale
                    value={{
                        priceMin: formValue.price_min as PriceMin,
                        priceMax: formValue.price_max as PriceMax
                    }}
                    onChange={({ priceMin, priceMax }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            price_min: priceMin,
                            price_max: priceMax
                        }));
                    }}
                />
            ) : (
                <SelectPriceToRent
                    value={{
                        priceMin: formValue.price_min as PriceMin,
                        priceMax: formValue.price_max as PriceMax,
                        priceFrequency: (formValue.price_frequency ||
                            'per_month') as string
                    }}
                    onChange={({ priceMin, priceMax, priceFrequency }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            price_min: priceMin,
                            price_max: priceMax,
                            price_frequency: priceFrequency
                        }));
                    }}
                />
            )}

            <div className={filterDividerStyle} />

            <SelectBeds
                value={{
                    bedsMin: formValue.beds_min as BedsMin,
                    bedsMax: formValue.beds_max as BedsMax
                }}
                onChange={({ bedsMin, bedsMax }) => {
                    updateFormValue((oldFormValue) => ({
                        ...oldFormValue,
                        beds_min: bedsMin,
                        beds_max: bedsMax
                    }));
                }}
            />

            <div className={filterDividerStyle} />
            <label className={`${filtersLabelStyle} ${fontGroups.ladle}`}>
                Property type
            </label>
            {isNewHomesDevelopersSearch ? (
                <SelectPropertyType
                    value={formValue.property_type as PropertyType}
                    onChange={(propertyType) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_type: propertyType
                        }));
                    }}
                />
            ) : (
                <PropertyTypeCheckboxes
                    value={{
                        propertyType: formValue.property_type as PropertyType,
                        propertySubType:
                            formValue.property_sub_type as PropertySubType
                    }}
                    onChange={({ propertySubType, propertyType }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_sub_type: propertySubType,
                            property_type: propertyType
                        }));
                    }}
                />
            )}
            <div className={stickyWrapperUpdateButtonStyle}>
                <Button
                    category="primary"
                    fw
                    onClick={handlePressUpdate}
                    data-testid="update-results-button"
                    title="Update"
                    disabled={!isReady}
                >
                    {updateResultsText}
                </Button>
            </div>
        </div>
    );
};
